import React, { useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { cn } from '../../../lib/utils';

interface EmailVerificationSuccessBannerProps {
  isEmailVerified: boolean;
  className?: string;
}

const CELEBRATION_STORAGE_KEY = 'email-verification-celebrated';
const CELEBRATION_DURATION = 2000; // 2 seconds

export function EmailVerificationSuccessBanner({ 
  isEmailVerified, 
  className 
}: EmailVerificationSuccessBannerProps) {
  const [showCelebration, setShowCelebration] = useState(false);
  const [hasBeenCelebrated, setHasBeenCelebrated] = useState(false);

  // Check if celebration has been shown before
  useEffect(() => {
    const celebratedTimestamp = localStorage.getItem(CELEBRATION_STORAGE_KEY);
    setHasBeenCelebrated(!!celebratedTimestamp);
  }, []);

  // Show celebration when email becomes verified
  useEffect(() => {
    if (isEmailVerified && !hasBeenCelebrated && !showCelebration) {
      setShowCelebration(true);
      
      // Mark as celebrated
      localStorage.setItem(CELEBRATION_STORAGE_KEY, Date.now().toString());
      setHasBeenCelebrated(true);
      
      // Auto-hide after celebration duration
      const timer = setTimeout(() => {
        setShowCelebration(false);
      }, CELEBRATION_DURATION);
      
      return () => clearTimeout(timer);
    }
  }, [isEmailVerified, hasBeenCelebrated, showCelebration]);

  // Don't show anything if not verified or already celebrated
  if (!isEmailVerified || (!showCelebration && hasBeenCelebrated)) {
    return null;
  }

  return (
    <Alert 
      className={cn(
        'border-green-200 bg-green-50 mb-6 transition-all duration-500',
        showCelebration ? 'animate-in slide-in-from-top-2' : '',
        className
      )}
    >
      <CheckCircle className="h-4 w-4 text-green-600" />
      <AlertDescription className="flex items-center">
        <div className="flex items-center gap-2 text-green-800">
          <span className="font-medium">
            🎉 Email verified successfully!
          </span>
          <span className="text-sm">
            You'll now receive weekly meal plans.
          </span>
        </div>
      </AlertDescription>
    </Alert>
  );
}